import { useEffect, useState } from "react";
import { listCustomerHealth, updateCustomerHealth } from "../api/support.ts";
import { useAuth } from "../context/auth.tsx";
import { Layout } from "../components/layout.tsx";

interface CustomerHealth {
  id: string;
  clientId: string;
  healthScore: number;
  status: string;
  openTickets: number;
  activeProjects: number;
  overdueInvoices: number;
  lastInteractionAt: string | null;
  renewalDate: string | null;
  notes: string | null;
  updatedAt: string;
}

const STATUS_COLOR: Record<string, string> = {
  HEALTHY: "text-green-400 bg-green-900/30",
  AT_RISK: "text-yellow-400 bg-yellow-900/30",
  CHURNED: "text-red-400 bg-red-900/30",
};

const SCORE_BAR_COLOR = (score: number) =>
  score >= 70 ? "bg-green-500" : score >= 40 ? "bg-yellow-500" : "bg-red-500";

export function SupportHealthPage() {
  const { token } = useAuth();
  const [clients, setClients] = useState<CustomerHealth[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [updating, setUpdating] = useState<string | null>(null);

  const reload = () => {
    setLoading(true);
    void listCustomerHealth(token, { status: statusFilter || undefined }).then((d) => {
      setClients((d as { items: CustomerHealth[] }).items ?? []);
      setLoading(false);
    });
  };

  useEffect(() => {
    reload();
  }, [statusFilter]);

  const refresh = async (clientId: string) => {
    setUpdating(clientId);
    await updateCustomerHealth(token, { clientId });
    setUpdating(null);
    reload();
  };

  return (
    <Layout>
      <div className="mx-auto max-w-6xl p-6">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Customer Health</h1>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded border border-gray-700 bg-gray-900 px-3 py-1.5 text-sm text-gray-300"
          >
            <option value="">All clients</option>
            <option value="HEALTHY">Healthy</option>
            <option value="AT_RISK">At Risk</option>
            <option value="CHURNED">Churned</option>
          </select>
        </div>
        {loading ? (
          <p className="text-gray-400">Loading...</p>
        ) : (
          <div className="space-y-3">
            {clients.length === 0 && (
              <p className="text-gray-400">
                No customer health records. Run health updates for your clients.
              </p>
            )}
            {clients.map((c) => (
              <div key={c.id} className="rounded-lg border border-gray-800 bg-gray-900 p-4">
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="mb-2 flex items-center gap-3">
                      <span className="font-mono text-sm font-medium text-white">{c.clientId}</span>
                      <span
                        className={`rounded px-2 py-0.5 text-xs ${STATUS_COLOR[c.status] ?? "bg-gray-800 text-gray-400"}`}
                      >
                        {c.status}
                      </span>
                      <span className="text-sm font-bold text-white">{c.healthScore}/100</span>
                    </div>
                    <div className="mb-2 h-1.5 w-full rounded-full bg-gray-800">
                      <div
                        className={`h-1.5 rounded-full transition-all ${SCORE_BAR_COLOR(c.healthScore)}`}
                        style={{ width: `${c.healthScore}%` }}
                      />
                    </div>
                    <div className="flex gap-4 text-xs text-gray-400">
                      <span>
                        Open tickets:{" "}
                        <span className={c.openTickets > 0 ? "text-yellow-400" : "text-gray-300"}>
                          {c.openTickets}
                        </span>
                      </span>
                      <span>
                        Active projects: <span className="text-gray-300">{c.activeProjects}</span>
                      </span>
                      <span>
                        Overdue invoices:{" "}
                        <span className={c.overdueInvoices > 0 ? "text-red-400" : "text-gray-300"}>
                          {c.overdueInvoices}
                        </span>
                      </span>
                      {c.renewalDate && (
                        <span>
                          Renewal:{" "}
                          <span className="text-gray-300">
                            {new Date(c.renewalDate).toLocaleDateString()}
                          </span>
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => void refresh(c.clientId)}
                    disabled={updating === c.clientId}
                    className="rounded bg-gray-800 px-2 py-1 text-xs text-gray-400 hover:text-white disabled:opacity-50"
                  >
                    {updating === c.clientId ? "..." : "Refresh"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
